import { Globe, Search, Share2, MousePointerClick, MessageSquareText, Star } from "lucide-react";

export const SERVICES = [
  {
    slug: "web-design",
    n: "01",
    title: "Website Design & Development",
    icon: Globe,
    short: "Conversion-first websites that load fast and turn visitors into customers.",
    points: ["Custom responsive design", "Lightning-fast performance", "Built to convert & rank"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
  },
  {
    slug: "seo",
    n: "02",
    title: "Google Search Engine Optimization",
    icon: Search,
    short: "Climb to page one and get found by customers actively searching for you.",
    points: ["Keyword & competitor research", "On-page & technical SEO", "Local map pack ranking"],
    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43",
  },
  {
    slug: "social",
    n: "03",
    title: "Social Media Marketing",
    icon: Share2,
    short: "Scroll-stopping content and community building across every platform.",
    points: ["Content strategy & creation", "Community management", "Growth-driven campaigns"],
    image: "https://images.unsplash.com/photo-1622549037543-49cf1ca0babc",
  },
  {
    slug: "ppc",
    n: "04",
    title: "Pay-Per-Click Advertising",
    icon: MousePointerClick,
    short: "Profitable Google & Meta ad campaigns engineered around real ROI.",
    points: ["Google & Meta Ads", "Landing page optimization", "Transparent ROI reporting"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
  },
  {
    slug: "email-sms",
    n: "05",
    title: "Email & SMS Marketing",
    icon: MessageSquareText,
    short: "Automated flows that nurture leads and drive repeat revenue on autopilot.",
    points: ["Automated email flows", "SMS broadcast campaigns", "Segmentation & A/B testing"],
    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43",
  },
  {
    slug: "reputation",
    n: "06",
    title: "Reputation Management",
    icon: Star,
    short: "Earn more 5-star reviews and protect the brand you've worked hard to build.",
    points: ["Review generation", "Response management", "Brand monitoring"],
    image: "https://images.unsplash.com/photo-1622549037543-49cf1ca0babc",
  },
];

export const STATS = [
  { value: "312%", label: "Avg. traffic growth" },
  { value: "4.2x", label: "Return on ad spend" },
  { value: "180+", label: "Small businesses grown" },
  { value: "$14M", label: "Client revenue driven" },
];

export const TESTIMONIALS = [
  { quote: "Massive Growth rebuilt our site and doubled our leads in 90 days. It genuinely changed our business.", name: "Sarah Mitchell", role: "Owner, Bloom Interiors" },
  { quote: "Our Google Ads finally make money. The ROI reporting is crystal clear and the team just gets it.", name: "David Chen", role: "Founder, Peak Fitness Co." },
  { quote: "We went from page 4 to the top of local search. Calls have never stopped since.", name: "Maria Lopez", role: "Director, Lopez Law Group" },
];

export const PROCESS = [
  { n: "01", title: "Discover", body: "We audit your brand, market and competitors to find the biggest, fastest growth levers." },
  { n: "02", title: "Strategize", body: "A custom growth blueprint mapped to your goals, budget and timeline — no cookie-cutter plans." },
  { n: "03", title: "Execute", body: "Our specialists build, launch and manage every channel with obsessive attention to detail." },
  { n: "04", title: "Scale", body: "We double down on what works, reporting transparently on the metrics that grow revenue." },
];

export const PACKAGES = [
  {
    name: "Massive Growth Starter",
    tagline: "Everything a local business needs to start generating real leads.",
    price: "$500",
    period: "/mo",
    lookupKey: "mgm_starter_monthly",
    highlight: false,
    cta: "Start growing",
    inheritLabel: null,
    features: [
      "Website / landing-page optimization",
      "Local SEO",
      "Google Business Profile optimization",
      "AI-assisted content",
      "Lead-generation campaign",
      "Lead tracking",
      "Monthly reporting",
      "Conversion optimization",
    ],
  },
  {
    name: "Massive Growth Pro",
    tagline: "A full-scale, AI-native growth engine for businesses ready to scale fast.",
    price: "$1,000",
    period: "/mo",
    lookupKey: "mgm_pro_monthly",
    highlight: true,
    cta: "Scale with Pro",
    inheritLabel: "Everything in Starter, plus:",
    features: [
      "Go High Level Access",
      "Paid advertising",
      "AI Voice Automation",
      "AI lead follow-up",
      "Automated email / SMS campaigns",
      "Reputation management",
      "Conversion optimization",
      "Marketing analytics",
      "Monthly strategy calls",
    ],
  },
];

export const TEMPLATES = [
  { business: "Restaurants & Dining", tag: "Hospitality", blurb: "Mouth-watering menus, reservations and reviews that fill every table.", image: "https://images.unsplash.com/photo-1651440204216-548382747b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { business: "Fitness & Gyms", tag: "Health & Wellness", blurb: "Class schedules, memberships and free-trial funnels that convert.", image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { business: "Real Estate", tag: "Property", blurb: "Stunning listings, IDX search and lead-capture built to sell homes.", image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { business: "Coffee Shops & Cafés", tag: "Food & Beverage", blurb: "Warm, inviting sites with online ordering and loyalty built in.", image: "https://images.unsplash.com/photo-1627743914480-5c0a391767f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { business: "Dental & Medical", tag: "Healthcare", blurb: "Trust-building sites with online booking and new-patient flows.", image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { business: "Salons & Spas", tag: "Beauty", blurb: "Elegant portfolios with instant booking and gift-card sales.", image: "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { business: "Law Firms", tag: "Professional Services", blurb: "Authoritative, credibility-first sites that turn clicks into consults.", image: "https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { business: "Auto & Repair", tag: "Automotive", blurb: "Service menus, quote requests and booking to keep the bays full.", image: "https://images.unsplash.com/photo-1619505372149-07875c35b313?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
];
